/**
 * ToolboxPreviewPanel - 工具箱预览浮层
 * 名片大小的浮层组件，展示用户工具箱中的所有物品
 * 提供管理入口按钮，点击后在新窗口打开完整的工具箱管理页面
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Package, AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

// 物品类型定义
export interface UserToolboxItem {
  id: number;
  name: string;
  item_type: string;
  tag_slug?: string | null;
}

// 分类常量及图标颜色 - 使用后端 category slug
const ITEM_TYPES = [
  {
    value: "medications",
    labelKey: "toolbox.categories.medications",
    fallbackLabel: "药品",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  },
  {
    value: "equipment",
    labelKey: "toolbox.categories.equipment",
    fallbackLabel: "设备",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  },
  {
    value: "water-conditioners",
    labelKey: "toolbox.categories.waterConditioners",
    fallbackLabel: "水质调理",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
  {
    value: "food-nutrition",
    labelKey: "toolbox.categories.foodNutrition",
    fallbackLabel: "饲料",
    color:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  {
    value: "testing-kits",
    labelKey: "toolbox.categories.testingKits",
    fallbackLabel: "测试剂",
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  },
  {
    value: "other",
    labelKey: "toolbox.categories.other",
    fallbackLabel: "其他",
    color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  },
] as const;

// Props 接口
interface ToolboxPreviewPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolboxData: Record<string, UserToolboxItem[]> | undefined;
  isLoading: boolean;
  isError: boolean;
  children: React.ReactNode;
}

/**
 * 计算工具箱中的物品总数
 */
function getTotalItemCount(
  data: Record<string, UserToolboxItem[]> | undefined,
): number {
  if (!data) return 0;
  return Object.values(data).reduce(
    (total, items) => total + (Array.isArray(items) ? items.length : 0),
    0,
  );
}

/**
 * 获取分类信息
 */
function getCategoryInfo(
  categorySlug: string,
  t: (key: string, fallback?: string) => string,
) {
  const found = ITEM_TYPES.find((item) => item.value === categorySlug);
  if (found) {
    return {
      value: found.value,
      label: t(found.labelKey, found.fallbackLabel),
      color: found.color,
    };
  }
  return {
    value: categorySlug,
    label: categorySlug,
    color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  };
}

const ToolboxPreviewPanel: React.FC<ToolboxPreviewPanelProps> = ({
  open,
  onOpenChange,
  toolboxData,
  isLoading,
  isError,
  children,
}) => {
  const { t } = useTranslation("assessments");

  const totalCount = getTotalItemCount(toolboxData);
  const isEmpty = !toolboxData || Object.keys(toolboxData).length === 0;

  const handleManageToolbox = () => {
    window.open("/app/toolbox", "_blank");
  };

  // 渲染加载状态
  const renderLoading = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );

  // 渲染错误状态
  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-6 text-center px-4">
      <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>
      <p className="text-sm text-muted-foreground">
        {t("toolbox.preview.loadError", "加载失败，请稍后重试")}
      </p>
    </div>
  );

  // 渲染空状态
  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-6 text-center px-4">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Package className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">
        {t("toolbox.preview.empty", "工具箱还是空的")}
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {t(
          "toolbox.preview.emptyHint",
          "添加您的水族用品，获得更精准的护理建议",
        )}
      </p>
    </div>
  );

  const renderItems = () => (
    <div className="space-y-4 p-1 pb-2">
      {Object.entries(toolboxData!).map(([type, items]) => {
        if (!Array.isArray(items) || items.length === 0) return null;
        const categoryInfo = getCategoryInfo(type, t);
        return (
          <div key={type}>
            <div className="flex items-center gap-2 mb-2">
              <Badge
                variant="secondary"
                className={`text-xs font-normal ${categoryInfo.color}`}
              >
                {categoryInfo.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {items.length}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {items.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex px-2 py-1 bg-muted/60 hover:bg-muted rounded-md text-xs transition-colors"
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 shadow-xl border-none bg-transparent"
        align="end"
        side="right"
        sideOffset={8}
      >
        <Card className="border shadow-none overflow-hidden flex flex-col h-[480px]">
          <CardHeader className="p-4 border-b bg-muted/30 pb-3 space-y-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Package className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-sm font-semibold">
                  {t("toolbox.preview.title", "我的工具箱")}
                </CardTitle>
                {!isLoading && !isError && !isEmpty && (
                  <CardDescription className="text-[10px]">
                    {totalCount} 件物品
                  </CardDescription>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-4">
                {isLoading && renderLoading()}
                {isError && !isLoading && renderError()}
                {!isLoading && !isError && isEmpty && renderEmpty()}
                {!isLoading && !isError && !isEmpty && renderItems()}
              </div>
            </ScrollArea>
          </CardContent>

          <CardFooter className="p-3 border-t bg-muted/10">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs gap-1.5 hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={handleManageToolbox}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t("toolbox.preview.manage", "管理工具箱")}
            </Button>
          </CardFooter>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export default ToolboxPreviewPanel;
